import React from 'react';
import type { PaginationProps } from 'antd';
import { Empty, Pagination, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { I18n } from '@aws-amplify/core';

import { ActionPlanCard } from './ActionPlanCard';
import { useActionPlanList } from './hooks/useActionPlanList';
import { useActionPlanActions } from './hooks/useActionPlanActions';
import { useApplicationContext } from '@/context/v1/Application/context';
import { ACTION_PLAN_PAGE_SIZE } from './constants';

import * as S from '../styles';

interface ActionPlanPanelContentProps {
	onEditActionPlan: (id: string) => void;
}

export function ActionPlanPanelContent({ onEditActionPlan }: Readonly<ActionPlanPanelContentProps>): JSX.Element {
	const { currentItem, total, page, setPage, isLoading } = useActionPlanList();
	const { handleDelete } = useActionPlanActions();
	const { company } = useApplicationContext();

	function getShowingStart() {
		if (total === 0) return 0;
		return (page - 1) * ACTION_PLAN_PAGE_SIZE + 1;
	}

	function getShowingEnd() {
		if (total === 0) return 0;
		return Math.min(page * ACTION_PLAN_PAGE_SIZE, total);
	}

	const renderPaginationItem: NonNullable<PaginationProps['itemRender']> = (_, type, element) => {
		if (type === 'prev') return <LeftOutlined />;
		if (type === 'next') return <RightOutlined />;
		return element;
	};

	if (isLoading) {
		return <Spin />;
	}

	return (
		<S.ActionPlanContent>
			{currentItem ? (
				<ActionPlanCard
					item={currentItem}
					companyId={company.id}
					onEdit={onEditActionPlan}
					onDelete={handleDelete}
				/>
			) : (
				<Empty description={I18n.get('No action plan created')} />
			)}
			{total > 0 && (
				<S.PaginationRow>
					<S.PaginationLabel>
						{I18n.get('Showing')} {getShowingStart()}-{getShowingEnd()} {I18n.get('of')} {total}
					</S.PaginationLabel>
					<S.ActionPlanPagination>
						<Pagination
							total={total}
							pageSize={ACTION_PLAN_PAGE_SIZE}
							current={page}
							onChange={setPage}
							showSizeChanger={false}
							itemRender={renderPaginationItem}
						/>
					</S.ActionPlanPagination>
				</S.PaginationRow>
			)}
		</S.ActionPlanContent>
	);
}
